<template>
  <div class="border border-gray-200 rounded-xl overflow-hidden bg-white">
    <div class="grid" :style="{ gridTemplateColumns: `34px repeat(${days.length}, 1fr)` }">
      <!-- Header: corner + tappable day cells (tap opens the day view) -->
      <div class="bg-gray-50 border-b border-gray-200" />
      <div
        v-for="d in days"
        :key="d.iso"
        class="border-b border-gray-200 border-s border-gray-100 py-[7px] px-0.5 text-center cursor-pointer"
        :class="d.iso === todayIso ? 'bg-primary-50' : 'bg-gray-50'"
        @click="emit('pick-day', d.jsDate)"
      >
        <div class="text-[10px]" :class="d.iso === todayIso ? 'text-primary-600' : 'text-gray-500'">{{ d.dow }}</div>
        <div class="text-[13px] font-semibold" :class="d.iso === todayIso ? 'text-primary-700' : 'text-gray-800'">{{ d.dateNum }}</div>
      </div>

      <!-- Hour labels column -->
      <div class="relative" :style="{ height: bodyHeight + 'px' }">
        <div
          v-for="h in hourLabels"
          :key="h"
          class="absolute right-1 text-[9px] text-gray-400 leading-none"
          :style="{ top: (h === gridStart ? y(h) + 3 : y(h) - 5) + 'px' }"
        >{{ h }}</div>
      </div>

      <!-- One timeline column per day -->
      <div
        v-for="d in days"
        :key="d.iso + '-body'"
        class="relative border-s border-gray-100"
        :style="{ height: bodyHeight + 'px', background: d.iso === todayIso ? 'rgba(136,100,238,.05)' : 'transparent' }"
      >
        <div
          v-for="h in hourLabels.slice(1)"
          :key="h"
          class="absolute inset-x-0 border-t border-gray-100"
          :style="{ top: y(h) + 'px' }"
        />
        <div
          v-if="d.iso === todayIso && nowHour !== undefined"
          class="absolute inset-x-0 h-[2px] bg-red-500 z-[3]"
          :style="{ top: y(nowHour) + 'px' }"
        />
        <div
          v-for="item in laidOut(d.iso)"
          :key="item.event.id"
          class="absolute rounded-md px-1 py-0.5 overflow-hidden cursor-pointer text-right z-[2]"
          :style="{
            top: item.top + 1 + 'px',
            height: item.height + 'px',
            right: `calc(${item.offsetPct}% + 2px)`,
            width: `calc(${item.widthPct}% - 4px)`,
            background: bgOf(item.event),
            border: colorOf(item.event)?.border,
            borderInlineStart: `3px solid ${fgOf(item.event)}`,
          }"
          @click="emit('event', item.event)"
        >
          <appointment-status-badge :event="item.event" size="0.75rem" class="absolute top-0.5 left-0.5" />
          <div class="flex items-center gap-0.5 min-w-0" :class="getAppointmentStatusMark(item.event) ? 'pe-3.5' : ''">
            <appointment-markers :event="item.event" size="0.55rem" />
            <div class="text-[10px] font-semibold leading-tight truncate min-w-0 flex-1" :style="{ color: fgOf(item.event) }">
              {{ primaryText(item.event) }}
            </div>
          </div>
          <div
            v-if="item.height >= 32"
            class="text-[9px] leading-tight opacity-80"
            :style="{ color: fgOf(item.event) }"
            dir="ltr"
          >{{ startLabel(item.event) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { AppointmentType, type ICalendarEvent } from '@torim-app/types';
import { DateTime } from 'luxon';
import AppointmentMarkers from './AppointmentMarkers.vue';
import AppointmentStatusBadge from './AppointmentStatusBadge.vue';
import { getAppointmentStatusMark } from './appointment-status-mark';

const props = defineProps<{
  days: Array<{ iso: string; dow: string; dateNum: number; jsDate: Date }>;
  events: ICalendarEvent[];
  startHour: number;
  endHour: number;
  todayIso: string;
  colorOf: (e: ICalendarEvent) => { wrapperBackground: string; lineBg: string; border?: string } | null;
  nowHour?: number;
  /** 1 = the default density; scales with the business's calendar row-height setting. */
  hourHeightScale?: number;
}>();
const emit = defineEmits<{ event: [e: ICalendarEvent]; 'pick-day': [d: Date] }>();

// The week grid packs seven days across, so it stays denser than the main calendar and
// follows the chosen row height proportionally.
const BASE_HOUR_HEIGHT = 44;
const HOUR_HEIGHT = computed(() => Math.round(BASE_HOUR_HEIGHT * (props.hourHeightScale ?? 1)));
const MIN_EVENT_HEIGHT = 18;
const ZONE = 'Asia/Jerusalem';

const parseDT = (s?: string) => (s ? DateTime.fromFormat(s, 'yyyy-MM-dd HH:mm', { zone: ZONE }) : null);
const startFloat = (e: ICalendarEvent): number => {
  const dt = parseDT(e.start);
  return dt?.isValid ? dt.hour + dt.minute / 60 : NaN;
};
const endFloat = (e: ICalendarEvent): number => {
  const dt = parseDT(e.end);
  return dt?.isValid ? dt.hour + dt.minute / 60 : startFloat(e) + (e.duration ?? 0.5);
};

// Extend the visible range so events outside working hours still fit.
const gridStart = computed(() => {
  let s = props.startHour;
  for (const e of props.events) { const es = startFloat(e); if (!Number.isNaN(es)) s = Math.min(s, Math.floor(es)); }
  return s;
});
const gridEnd = computed(() => {
  let en = props.endHour;
  for (const e of props.events) { const ee = endFloat(e); if (!Number.isNaN(ee)) en = Math.max(en, Math.ceil(ee)); }
  return en;
});
const hourLabels = computed(() => {
  const out: number[] = [];
  for (let h = gridStart.value; h <= gridEnd.value - 1; h++) out.push(h);
  return out;
});
const bodyHeight = computed(() => (gridEnd.value - gridStart.value) * HOUR_HEIGHT.value);
const y = (t: number) => (t - gridStart.value) * HOUR_HEIGHT.value;

const isManual = (e: ICalendarEvent) => e.type === AppointmentType.MANUAL || e.type === AppointmentType.OUT_OF_OFFICE;
const primaryText = (e: ICalendarEvent) => (isManual(e) ? e.title : (e.customerName || e.title));
const startLabel = (e: ICalendarEvent) => parseDT(e.start)?.toFormat('HH:mm') ?? '';
const bgOf = (e: ICalendarEvent) => props.colorOf(e)?.wrapperBackground ?? '#edeafd';
const fgOf = (e: ICalendarEvent) => props.colorOf(e)?.lineBg ?? '#6831d0';

interface LaidOut { event: ICalendarEvent; top: number; height: number; offsetPct: number; widthPct: number }

// Overlapping events (double bookings) share the column width in lanes.
const layoutDay = (evs: ICalendarEvent[]): LaidOut[] => {
  const items = evs
    .map(e => ({ e, s: startFloat(e), en: endFloat(e) }))
    .filter(x => !Number.isNaN(x.s) && x.en > x.s)
    .sort((a, b) => a.s - b.s || a.en - b.en);

  const out: LaidOut[] = [];
  let i = 0;
  while (i < items.length) {
    let clusterEnd = items[i].en;
    const cluster = [items[i]];
    let j = i + 1;
    while (j < items.length && items[j].s < clusterEnd) {
      cluster.push(items[j]);
      clusterEnd = Math.max(clusterEnd, items[j].en);
      j++;
    }
    const laneEnds: number[] = [];
    const laneOf: number[] = [];
    cluster.forEach((item, k) => {
      let lane = laneEnds.findIndex(end => end <= item.s + 1e-6);
      if (lane === -1) { lane = laneEnds.length; laneEnds.push(item.en); } else laneEnds[lane] = item.en;
      laneOf[k] = lane;
    });
    const widthPct = 100 / laneEnds.length;
    cluster.forEach((item, k) => {
      out.push({
        event: item.e,
        top: y(item.s),
        height: Math.max(MIN_EVENT_HEIGHT, (item.en - item.s) * HOUR_HEIGHT.value - 2),
        offsetPct: widthPct * laneOf[k],
        widthPct,
      });
    });
    i = j;
  }
  return out;
};
const laidOut = (iso: string): LaidOut[] =>
  layoutDay(props.events.filter(e => parseDT(e.start)?.toISODate() === iso));
</script>
